import type { MetaFunction } from '@remix-run/node';
import { Link } from '@remix-run/react';
import { memo } from 'react';
import { buildMeta } from '~/utils/meta.js';

export const meta: MetaFunction = (args) => {
  return buildMeta(args, { title: 'Privacy Policy | hostU' });
};

const policy = memo(() => {
  return (
    <div className="mx-auto max-w-[800px] px-4 py-16">
      <div className="py-8 font-bold text-4xl">
        <p>hostU Privacy Policy</p>
      </div>
      <div className="space-y-6 text-xl leading-10">
        <p> Last Updated: November 11, 2023</p>
        <p> Thank you for using HostU!</p>
        <p>
          hostU, Inc. (<strong>"Company"</strong> or
          <strong>"We"</strong>) respect your privacy and are committed to protecting it through our
          compliance with this policy.
        </p>
        <p>
          This policy describes the types of information we may collect from you or that you may
          provide when you visit the website <Link to="/">https://www.joinhostu.com </Link> (our "
          <strong>Website</strong>") and our practices for collecting, using, maintaining,
          protecting, and disclosing that information.
        </p>
        <p>This policy applies to information we collect:</p>
        <p>• On this Website.</p>
        <p>• In email, text, and other electronic messages between you and this Website.</p>
        <p>
          • When you interact with our advertising and applications on third-party websites and
          services, if those applications or advertising include links to this policy.
        </p>
        <p>It does not apply to information collected by:</p>
        <p>
          • Us offline or through any other means, including on any other website operated by
          Company or any third party[ (including our affiliates and subsidiaries)]; or
        </p>
        <p>
          • Any third party [(including our affiliates and subsidiaries)], including through any
          application or content (including advertising) that may link to or be accessible from or
          through the Website.
        </p>
        <p>
          Please read this policy carefully to understand our policies and practices regarding your
          information and how we will treat it. If you do not agree with our policies and practices,
          your choice is not to use our Website. By accessing or using this Website, you agree to
          this privacy policy. This policy may change from time to time. Your continued use of this
          Website after we make changes is deemed to be acceptance of those changes, so please check
          the policy periodically for updates.
        </p>
        <p>
          <strong>1. DEFINITIONS</strong>
        </p>
        <p>
          Undefined terms in this Privacy Policy have the same definition as in our{' '}
          <Link to="/terms-of-service">
            <u>Terms of Service</u>
          </Link>
          ("<strong>Terms</strong>").
        </p>
        <p>
          <strong>2. PERSONAL INFORMATION WE COLLECT</strong>
        </p>
        <p>
          <strong>2.1 Information Needed to Use the hostU Platform.</strong>
        </p>
        <p>
          We collect personal information about you when you use the hostU Platform. Without it, we
          may not be able to provide all services requested. This information includes:
        </p>
        <ul className="list-disc pl-6">
          <li>
            <strong>Contact, Account, and Profile Information</strong>. Such as your first name,
            last name, phone number, postal address, email address, date of birth, and profile
            photo, some of which will depend on the features you use.
          </li>
          <li>
            <strong>Identity Verification and Payment Information</strong>. Such as images of your
            government-issued ID (as permitted by applicable laws), your ID number or other
            verification information, a selfie when we verify your ID, bank account or payment
            account information. ​​If you are not an hostU user, we may receive payment information
            relating to you, such as when an hostU user provides your payment card to complete a
            booking. If a copy of your ID is provided to us, we may scan, use, and store information
            contained in your ID to verify your identity and for security purposes.
          </li>
        </ul>
        <p>
          <strong>2.2 Information You Choose to Give Us</strong>.
        </p>
        <p>
          You can choose to provide us with additional personal information. This information may
          include:
        </p>
        <ul className="list-disc pl-6">
          <li>
            <strong>Additional Profile Information</strong>. Such as gender, preferred language(s),
            city, and personal description. Some of this information as indicated in your account
            settings is part of your public profile page and will be publicly visible.
          </li>
          <li>
            <strong>Information About Others.</strong>
            <strong> </strong>Such as a payment instrument belonging to another person or
            information about a co-traveler. By providing us with personal information about others,
            you certify that you have permission to provide that information to hostU for the
            purposes described in this Privacy Policy and you have shared the hostU Privacy Policy
            with them.
          </li>
          <li>
            <strong>Address Book Contact Information</strong>.
          </li>
          <li>
            <strong>Other Information</strong>. Such as when you fill in a form, add information to
            your account, respond to surveys, post to community forums, participate in promotions,
            communicate with hostU Support and other Members, import or manually enter address book
            contacts, provide your address and/or geolocation, or share your experience with us.
            This may include health information if you choose to share it with us.
          </li>
        </ul>
        <p>
          <strong>
            2.3 Information Automatically Collected by Using the hostU Platform and Our Payment
            Services
          </strong>
          .
        </p>
        <p>
          When you use the hostU Platform and Payment Services, we automatically collect personal
          and other information. This information may include:
        </p>
        <ul className="list-disc pl-6">
          <li>
            <strong>Geolocation Information</strong>. Such as precise or approximate location
            determined from your IP address, mobile or other device’s GPS, or other information you
            share with us, depending on your device settings. We may also collect this information
            when you’re not using the app if you enable this through your settings or device
            permissions.
          </li>
          <li>
            <strong>Usage Information</strong>. Such as the pages or content you view, searches for
            Listings, bookings you have made, additional services you have added, and other actions
            on the hostU Platform.
          </li>
          <li>
            <strong>Log Data and Device Information</strong>. Such as details about how you’ve used
            the hostU Platform (including if you clicked on links to third-party applications), IP
            address, access dates and times, hardware and software information, device information,
            device event information, unique identifiers, crash data, and the pages you’ve viewed or
            engaged with before or after using the hostU Platform. We may collect this information
            even if you haven’t created a hostU account or logged in.
          </li>
          <li>
            <strong>Payment Transaction Information</strong>. Such as payment instrument used, date
            and time, payment amount, payment instrument expiration date and billing postcode, email
            address, IBAN information, your address, and other related transaction details.
          </li>
          <li>
            <strong>Third</strong>-<strong>Party Service Providers</strong>. To the extent we use an
            third party as a payment processor, the terms, conditions, and policies of that service
            provider will also apply.
          </li>
        </ul>
        <p>
          <strong>3. HOW WE USE INFORMATION WE COLLECT</strong>
        </p>
        <p>We use personal information as outlined in this Privacy Policy.</p>
        <p>
          <strong>3.1 Provide, Improve, and Develop the hostU Platform</strong>. We may process this
          information to:
        </p>
        <ul className="list-disc pl-6">
          <li>enable you to access the hostU Platform and make and receive payments,</li>
          <li>enable you to communicate with other Members,</li>
          <li>process your request,</li>
          <li>perform analytics, debug, and conduct research,</li>
          <li>provide customer service training,</li>
          <li>send you messages, updates, security alerts, and account notifications,</li>
          <li>process, handle, or assess insurance claims or similar claims,</li>
          <li>
            personalize and customize your experience based on your interactions with the hostU
            Platform, your search and booking history, your profile information and preferences, and
            other content you submit, and
          </li>
          <li>enable your use of our enterprise products and accommodation services.</li>
        </ul>
        <p>
          If you provide us with your contacts’ information, such as your friends or co-travelers,
          we may process this information to: (i) facilitate your referral invitations, (ii) share
          your trip details and facilitate trip planning, (iii) detect and prevent fraud, and (iv)
          facilitate your requests or for any other purpose you authorize.
        </p>
        <p>
          <strong>3.2 Create and Maintain a Trusted and Safer Environment</strong>. We may process
          this information to:
        </p>
        <ul className="list-disc pl-6">
          <li>
            detect and prevent fraud, spam, abuse, security and safety incidents, and other harmful
            activities,
          </li>
          <li>conduct fraud prevention, security investigations, and risk assessments,</li>
          <li>verify or authenticate information provided by you,</li>
          <li>
            conduct checks against databases and other information sources, including background
            checks,
          </li>
          <li>
            comply with our legal obligations, protect the health and well-being of our Guests,
            Hosts, Hosts’ employees, and members of the public,
          </li>
          <li>
            resolve disputes with our Members, including sharing information with your co-Host(s) or
            additional Guests about disputes related to your role as a co-Host(s) or additional
            Guests,
          </li>
          <li>enforce our agreements with third parties,</li>
          <li>
            comply with law, respond to legal requests, prevent harm, and protect our rights (see
            section 4.5), and
          </li>
          <li>
            assess or evaluate your interactions with the hostU Platform and information obtained
            from third parties. In limited cases, automated processes, which analyze your account
            and activities on the hostU Platform as well as information in relation to activities on
            and off the hostU Platform that can be associated with you, could restrict or suspend
            your access to the hostU Platform if such processes detect activity that may pose a
            safety or other risk to hostU, our community, or third parties. If you would like to
            challenge decisions based on automated processes, please contact us via the Contact
            Information section below.
          </li>
        </ul>
        <p>
          <strong>
            3.3 Provide, Personalize, Measure, and Improve our Advertising and Marketing
          </strong>
          . We may process this information to:
        </p>
        <ul className="list-disc pl-6">
          <li>
            send you promotional messages, marketing, advertising, and other information based on
            your preferences and social media advertising through social media platforms,
          </li>
          <li>personalize, measure, and improve our advertising,</li>
          <li>
            analyze characteristics and preferences to send you promotional messages, marketing,
            advertising, and other information that we think might be of interest to you,
          </li>
          <li>invite you to events and relevant opportunities, and </li>
          <li>
            send, with your consent, promotional messages, marketing, advertising, and other
            information that may be of interest to you based on your preferences.
          </li>
        </ul>
        <p>
          <strong>3.4 Analyzing and Sharing Your Communications.</strong>
        </p>
        <p>
          We may review, scan, or analyze your communications on the hostU Platform for reasons
          outlined in the “How We Use Information We Collect” section of this policy, including
          fraud prevention, security investigations, risk assessment, regulatory compliance, product
          development, research, analytics, and customer support purposes. For example, as part of
          our fraud prevention efforts, we scan and analyze messages to mask contact information and
          references to other sites, and subject to applicable law, we scan and analyze all images
          uploaded by users to the hostU Platform in message threads, profiles, listings, and
          experiences for certain illegal or inappropriate activities (such as evidence of child
          exploitation) for the purpose of identifying and reporting content violations to
          appropriate authorities. In some cases, we may also scan, review, or analyze messages to
          debug, improve, and expand product offerings. We use automated methods where reasonably
          possible. Occasionally we may need to manually review communications, such as for fraud
          investigations and customer support, or to assess and improve the functionality of these
          automated tools. We will not review, scan, or analyze your messaging communications to
          send third-party marketing messages to you and we will not sell reviews or analyses of
          these communications. We may also share your communications as set out in the “Sharing &
          Disclosure” section.
        </p>
        <p>
          <strong>3.5 Linking Third-Party Accounts.</strong>
        </p>
        <p>
          You can link your hostU account with certain third-party services like social networks.
          Your contacts on these third-party services are referred to as “Friends.” When you direct
          the data sharing by creating this link:
        </p>
        <ul className="list-disc pl-6">
          <li>
            some of the information provided to us from linking accounts may be published on your
            public profile,
          </li>
          <li>
            your activities on the hostU Platform may be displayed to your Friends on the hostU
            Platform and/or that third-party service,
          </li>
          <li>
            a link to your public profile on that third-party service may be included in your hostU
            public profile,
          </li>
          <li>
            other users may be able to see any Friends that you may have in common with them, or
            that you are a Friend of their Friend if applicable,
          </li>
          <li>
            other users may be able to see any schools, hometowns, or other groups you have in
            common with them as listed on your linked social networking service,
          </li>
          <li>
            information you provide to us from the linking of your accounts may be stored,
            processed, and transmitted for fraud prevention, security investigations, and risk
            assessment purposes, and
          </li>
          <li>
            publication and display of information that you provide to the hostU Platform through
            this linkage is subject to your settings and authorizations on the hostU Platform and
            the third-party service.
          </li>
        </ul>
        <p>
          <strong>4. SHARING & DISCLOSURE</strong>
        </p>
        <p>
          <strong>4.1 Sharing With Your Consent or at Your Direction</strong>.
        </p>
        <p>
          Where you provide consent, we share your information as described at the time of consent,
          such as when authorizing a third-party application or website to access your hostU account
          or participating in promotional activities by hostU partners or third parties.
        </p>
        <p>
          Where permissible under applicable law, we may use certain information about you, such as
          your email address, that we share with social media platforms after de-identifying it to
          generate leads, drive traffic to hostU, or otherwise promote our products and services.
        </p>
        <p>
          <strong>4.2 Sharing Between Members.</strong>
        </p>
        <p>
          To help facilitate bookings or other interactions between Members (who may be located in,
          or use service providers located in, jurisdictions with varying levels of data
          protection), we may share information in situations such as:
        </p>
        <ul className="list-disc pl-6">
          <li>
            Between Guest(s) and Host(s) when:
            <ul className="list-disc pl-6">
              <li>
                A booking request is made, such as profile, name, and other information you choose
                to share and submit.
              </li>
              <li>
                A booking is confirmed, additional information is shared to assist with coordinating
                the stay, including but not limited to contact information, leasing information, and
                Listing address.
              </li>
              <li>
                You communicate with a Member, such as your name, profile picture, and message
                content.
              </li>
            </ul>
          </li>
          <li>
            Between Guests when:
            <ul className="list-disc pl-6">
              <li>
                You as a Guest invite additional Guests to a booking, certain information is shared
                with each additional Guest, such as your name, travel dates, Host name, Listing
                details, Accommodation address, and other related information.
              </li>
            </ul>
          </li>
          <li>
            Between Hosts when:
            <ul className="list-disc pl-6">
              <li>
                You accept an invitation to host with another Member, you authorize those other
                Member(s), such as co-Hosts, to access and update your information and Member
                Content, such as certain information like your full name, phone number, Listing
                address, calendar, Listing information, Listing photos, and email address.
              </li>
            </ul>
          </li>
        </ul>
        <p>
          <strong>
            4.3 Information You Publish in Profiles, Listings, and Other Public Information
          </strong>
          .
        </p>
        <p>You can make certain information publicly visible to others, such as:</p>
        <ul className="list-disc pl-6">
          <li>
            Your public profile page, which includes your profile photo, first name (or initials
            where applicable), description, and city.
          </li>
          <li>
            Listing pages that include information such as precise location description, calendar
            availability, profile photo, aggregated demand information (like page views over a
            period of time), and additional information you choose to share.
          </li>
          <li>Reviews, ratings, and other public feedback.</li>
          <li>Content in a community or discussion forum, blog, or social media post.</li>
        </ul>
        <p>
          We may display parts of your public profile and other Content you make available to the
          public like Listing details on third-party sites, platforms, and apps.
        </p>
        <p>
          Information you share publicly on the hostU Platform may be indexed through third-party
          search engines. In some cases, you may opt-out of this feature in your account settings.
        </p>
        <p>
          <strong>4.4 Host Service Providers</strong>.
        </p>
        <p>
          Hosts may use third-party services to help manage or deliver their services, such as
          cleaning services or lock providers. Hosts may use features on the hostU Platform to share
          information about the Guest (like check-in and check-out information, Guest name, Guest
          phone number) with such third-party service providers.
        </p>
        <p>
          <strong>
            4.5 Complying with Law, Responding to Legal Requests, Preventing Harm and Protecting Our
            Rights
          </strong>
          .
        </p>
        <p>
          We may disclose your information to courts, law enforcement, governmental or public
          authorities, tax authorities, authorized third parties, or other Members, if and to the
          extent we are required or permitted to do so by law or where disclosure is reasonably
          necessary to: (i) comply with our legal obligations, (ii) comply with a valid legal
          request (such as a subpoena or court order) or to respond to claims asserted against
          hostU, (iii) respond to a valid legal request relating to a criminal investigation to
          address alleged or suspected illegal activity, or to respond to or address any other
          activity that may expose us, you, or any other of our users to legal or regulatory
          liability, (iv) enforce and administer our agreements with Members, or (v) protect the
          rights, property or personal safety of hostU, its employees, its Members, or members of
          the public.
        </p>
        <p>
          Where appropriate, we may notify Members about legal requests unless: (i) providing notice
          is prohibited by the legal process itself, by court order we receive, or by applicable
          law, or (ii) we believe that providing notice would be futile, ineffective, create a risk
          of injury or bodily harm to an individual or group, or create or increase a risk of fraud
          upon or harm to hostU, our Members, or expose hostU to a claim of obstruction of justice.
        </p>
        <p>
          Where legally required or permissible according to applicable law, we may disclose Hosts’
          and/or Guests’ information to tax authorities or other governmental agencies for the
          purpose of the tax authorities’ determination of proper compliance with relevant tax
          obligations. Relevant tax obligations include hostU’s tax obligations on its service fees,
          its facilitation of taxes on accommodations and withholding taxes, and Hosts’ individual
          tax obligations. Information that may be disclosed includes, but is not limited to, Host
          and Guest names, listing addresses, Host addresses, tax/business identification number(s),
          date of birth, and/or contact information, property parcel identification numbers, payout
          information, transaction dates and amounts, number of nights and Guests booked, gross and
          net booking value and payout amounts, taxes collected by hostU on behalf of Guests and
          Hosts, to the extent any of this information is known by hostU.
        </p>
        <p>
          In jurisdictions where hostU facilitates or requires a registration, notification, permit,
          or license application or number of a Host with a governmental authority, we may share
          information of participating Hosts with the relevant authority, both during the
          application process, when the Listing is published, and periodically thereafter, such as
          the Host’s full name and contact details, Accommodation address, tax identification
          number, registration, permit, or license number, Listing details, reservation information,
          and number of nights booked subject to applicable laws.
        </p>
        <p>
          <strong>4.6 Programs with Managers and Owners</strong>.
        </p>
        <p>
          We may share personal information of Hosts and Guests with landlords, management
          companies, homeowners' associations, property owners, and/or property managers (“
          <strong>Building Management</strong>”), such as booking information and information
          related to compliance with applicable laws, in order to facilitate programs with Building
          Management. For example, guest booking and personal information, including guest contact
          information, may be shared with the Building Management of the building, complex, or
          community where a host lives and/or the listing is located to facilitate hosting services,
          compliance with applicable laws, security, billing, and other services.
        </p>
        <p>
          <strong>4.7 Host Information Provided to hostU for Work Customers</strong>.
        </p>
        <p>
          If a booking is designated as being for a business or work purpose and (1) is made by a
          Guest affiliated with an Enterprise and (2) the Enterprise is enrolled in hostU for Work,
          we may disclose information related to the booking to the Enterprise (e.g., name of the
          Host, Accommodation address, booking dates, Listing details, etc.) to the extent necessary
          for the adequate performance of hostU’s contract with the Enterprise and to provide the
          services. At the request of the Enterprise or the Guest, we may also share this
          information with third parties engaged by the Enterprise to provide support services.
        </p>
        <p>
          <strong>4.8 Service Providers</strong>.
        </p>
        <p>
          We may share personal information with affiliated and unaffiliated service providers
          (including their service providers) to help us run our business and for their compliance
          purposes, including those that help us: (i) verify your identity or authenticate your
          identification documents, (ii) check information against public databases, (iii) conduct
          background checks, fraud prevention, security investigations, and risk assessments, (iv)
          perform product development, maintenance, and debugging, (v) allow the provision of the
          hostU Services through third-party platforms and software tools (e.g., through the
          integration with our APIs), (vi) provide customer service, advertising, or payments
          services, (vii) offer additional services you select, (viii) process, handle, or assess
          insurance claims or similar claims, or (ix) review, scan, and analyze communications on
          the hostU Platform for certain purposes (such as evidence of child exploitation). See the
          Analyzing and Sharing Your Communications section for additional information. These
          providers are contractually bound to protect your personal information and have access to
          your personal information to perform these tasks. Other Members can use services other
          than hostU to process your data. These can include email or reservation management
          software. Such services are outside of hostU’s control and will be subject to applicable
          laws around the world with varying levels of data protection.
        </p>
        <p>
          <strong>4.9 Business Transfers</strong>.
        </p>
        <p>
          If hostU undertakes or is involved in any merger, acquisition, reorganization, sale of
          assets, bankruptcy, or insolvency event, then we may sell, transfer, or share some or all
          of our assets, including your information in connection with such transaction or in
          contemplation of such transaction (e.g., due diligence). In this event, we will notify you
          before your personal information is transferred and becomes subject to a different privacy
          policy.
        </p>
        <p>
          <strong>5. OTHER IMPORTANT INFORMATION</strong>
        </p>
        <p>5.1 Third-Party Partners & Integrations.</p>
        <p>
          Parts of hostU may link to third-party services. hostU does not own or control these third
          parties. When you interact with these third parties and choose to use their service, you
          are providing your information to them. Your use of these services is subject to the
          privacy policies of those providers.
        </p>
        <p>
          <strong>6. YOUR RIGHTS</strong>
        </p>
        <p>
          You can exercise any of the rights described in this section consistent with applicable
          law. We may ask you to verify your identity and request before taking further action on
          your request.
        </p>
        <p>
          <strong>6.1 Managing Your Information</strong>.
        </p>
        <p>
          You can access and update some of your personal information through your Account settings.
          You are responsible for keeping your personal information up to date.
        </p>
        <p>
          <strong>6.2 Data Access and Portability</strong>.
        </p>
        <p>
          In some jurisdictions, applicable law may entitle you to request certain copies of your
          personal information or information about how we handle your personal information, request
          copies of personal information that you have provided to us in a structured, commonly
          used, and machine-readable format, and/or request that we transmit this information to
          another service provider (where technically feasible).
        </p>
        <p>
          <strong>6.3 Data Erasure</strong>.
        </p>
        <p>
          In some jurisdictions, you can request that your personal information be deleted. Please
          note that if you request deletion of your personal information, or if your account is
          suspended, terminated, or voluntarily closed:
        </p>
        <ul className="list-disc pl-6">
          <li>
            We may retain your personal information as necessary for our legitimate business
            interests, such as prevention of money laundering, fraud detection and prevention, and
            enhancing safety. For example, if we suspend an hostU Account for fraud or safety
            reasons, we may retain information from that hostU Account to prevent that Member from
            opening a new hostU Account in the future.
          </li>
          <li>
            We may retain and use your personal information to the extent necessary to comply with
            our legal obligations. For example, hostU may keep information for tax, legal reporting,
            and auditing obligations.
          </li>
          <li>
            Information you have shared with others (e.g., Reviews, forum postings) may continue to
            be publicly visible on hostU, even after your hostU Account is canceled.
          </li>
          <li>
            Because we take measures to protect data from accidental or malicious loss and
            destruction, residual copies of your personal information may not be removed from our
            backup systems for a limited period of time.
          </li>
        </ul>
        <p>
          <strong>7. SECURITY</strong>
        </p>
        <p>
          While no organization can guarantee perfect security, we are continuously implementing and
          updating administrative, technical, and physical security measures to help protect your
          information against unlawful or unauthorized access, loss, destruction, or alteration.
        </p>
        <p>
          <strong>8. CHANGES TO THIS PRIVACY POLICY</strong>
        </p>
        <p>
          We reserve the right to modify this Privacy Policy at any time in accordance with
          applicable law. If we do so, we will post the revised Privacy Policy and update the “Last
          Updated” date at the top. In case of material changes, we will also provide you with
          notice of the modification by email at least thirty (30) days before the effective date.
          If you disagree with the revised Privacy Policy, you can cancel your Account. If you do
          not cancel your Account before the date the revised Privacy Policy becomes effective, your
          continued access to or use of the hostU Platform will be subject to the revised Privacy
          Policy.
        </p>
        <p>
          <strong>9. CONTACT INFORMATION</strong>
        </p>
        <p>
          To ask questions or comment about this privacy policy and our privacy practices, contact
          us at:
        </p>
        <p>
          <a href="mailto:support@joinhostu.com">support@joinhostu.com</a>
        </p>
      </div>
    </div>
  );
});

export default policy;
